//#region Imports

import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { environment } from '../../../environments/environment';
import { HttpAsyncService } from '../../modules/http-async/services/http-async.service';
import { getCrudErrors } from '../../shared/utils/functions';
import { Subscription } from 'rxjs';
import { UserProxy } from '../../models/proxys/user.proxy';
import { apiRoutes } from '../../../environments/api-routes';
import { UserService } from '../../services/user/user.service';

//#endregion

@Component({
  selector: 'ngx-update-password',
  templateUrl: './update-password.component.html',
  styleUrls: ['./update-password.component.scss'],
})
export class UpdatePasswordComponent implements OnInit, OnDestroy {

  //#region Constructor

  constructor(
    protected formBuilder: FormBuilder,
    private readonly dialogRef: NbDialogRef<UpdatePasswordComponent>,
    private readonly http: HttpAsyncService,
    private readonly toast: NbToastrService,
    private readonly userService: UserService,
  ) {
    this.formGroup = formBuilder.group({
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required],
    });

    const passwordControl = this.formGroup.get('password');
    const confirmPasswordControl = this.formGroup.get('confirmPassword');

    if (passwordControl) {
      this.passwordSubscription = passwordControl.valueChanges.subscribe(() => {
        this.validatePassword();
      });
    }

    if (confirmPasswordControl) {
      this.confirmPasswordSubscription = confirmPasswordControl.valueChanges.subscribe(() => {
        this.validatePassword();
      });
    }
  }

  //#endregion

  //#region Properties

  public isLoading: boolean = false;
  public hidePassword: boolean = true;
  public hideConfirmPassword: boolean = true;
  public isPasswordOk: boolean = false;
  public formGroup: FormGroup;
  private passwordSubscription: Subscription = new Subscription();
  private confirmPasswordSubscription: Subscription = new Subscription();
  private dialogCloseSubscription: Subscription = new Subscription();

  //#endregion

  //#region LifeCycle Events

  public ngOnInit(): void {
    this.dialogCloseSubscription = this.dialogRef.onClose.subscribe(() => {
      this.userService.setShouldRequestPasswordChange(false);
    });
  }

  public ngOnDestroy(): void {
    this.passwordSubscription.unsubscribe();
    this.confirmPasswordSubscription.unsubscribe();
    this.dialogCloseSubscription.unsubscribe();
    this.userService.setShouldRequestPasswordChange(false);
  }

  //#endregion

  //#region Methods

  public async onSubmit(): Promise<void> {
    if (this.isLoading)
      return;

    const payload = this.formGroup.getRawValue();

    if (payload.password !== payload.confirmPassword)
      return void this.toast.danger('As senhas não conferem...', 'Oops...');

    const currentUser = this.userService.getCurrentUser();
    this.isLoading = true;

    const { error, success: updatedUser } = await this.http.put<UserProxy>(
      apiRoutes.users.password.replace('{id}', currentUser?.id ? currentUser?.id.toString() : '0'),
      { newPassword: payload.password },
    );

    if (error) {
      this.isLoading = false;
      return void this.toast.danger(getCrudErrors(error)[0], 'Oops...');
    }

    let userToSave: UserProxy | null = updatedUser || null;

    if (!userToSave) {
      const { error: userError, success: userFromMe } = await this.http.get<UserProxy>(apiRoutes.users.me);

      if (userError) {
        this.isLoading = false;
        this.userService.setShouldRequestPasswordChange(false);
        this.userService.refreshCurrentUser();
        this.toast.success('Senha atualizada com sucesso!', 'Sucesso');
        this.dialogRef.close();
        return;
      }

      userToSave = userFromMe || null;
    }

    if (userToSave) {
      const processedUser = this.userService.processUserFromApi(userToSave);
      this.userService.saveUserToLocalStorage(processedUser);
      this.userService.setShouldRequestPasswordChange(false);
      this.userService.refreshCurrentUser();
    } else {
      this.userService.saveUserToLocalStorage(currentUser);
      this.userService.setShouldRequestPasswordChange(false);
      this.userService.refreshCurrentUser();
    }

    this.isLoading = false;
    this.toast.success('Senha atualizada com sucesso!', 'Sucesso');
    this.dialogRef.close();
  }

  public validatePassword(): void {
    const password: string = this.formGroup.get('password')?.value || '';
    const confirmPassword: string = this.formGroup.get('confirmPassword')?.value || '';

    if (!password || !confirmPassword) {
      this.isPasswordOk = false;
      return;
    }

    const pattern = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}/;
    const isPasswordValid = pattern.test(password);
    const passwordsMatch = password === confirmPassword;

    this.isPasswordOk = isPasswordValid && passwordsMatch;
  }

  //#endregion

}
